import { Injectable } from '@angular/core';
import { BehaviorSubject, Observable, combineLatest, map, switchMap, of, firstValueFrom } from 'rxjs';
import { AuthService } from './auth.service';
import { CompanyService } from './company.service';
import { Company } from '../models/company.model';

export interface CompanyContext {
  isImpersonating: boolean;
  currentCompany: Company | null;
  originalUser: any;
}

@Injectable({
  providedIn: 'root'
})
export class CompanyContextService {
  private currentCompanyId$ = new BehaviorSubject<string | null>(null);
  private isImpersonating$ = new BehaviorSubject<boolean>(false);

  constructor(
    private authService: AuthService,
    private companyService: CompanyService
  ) {
    // Registrarse con AuthService para cleanup en logout
    this.authService.setCompanyContextService(this);

    // Restaurar contexto al inicializar
    this.restoreContext();
  }

  // Observable del contexto completo
  get context$(): Observable<CompanyContext> {
    return combineLatest([
      this.isImpersonating$,
      this.currentCompanyId$,
      this.authService.currentUser$
    ]).pipe(
      switchMap(([isImpersonating, companyId, user]) => {
        if (isImpersonating && companyId) {
          return this.companyService.findOne(companyId).pipe(
            map(company => ({
              isImpersonating: true,
              currentCompany: company,
              originalUser: user
            }))
          );
        }
        return of({
          isImpersonating: false,
          currentCompany: null,
          originalUser: user
        });
      })
    );
  }

  // Observables individuales para facilitar el uso
  get isImpersonatingObservable(): Observable<boolean> {
    return this.isImpersonating$.asObservable();
  }

  get currentCompany$(): Observable<Company | null> {
    return this.currentCompanyId$.pipe(
      switchMap(companyId => {
        if (companyId) {
          return this.companyService.findOne(companyId);
        }
        return of(null);
      })
    );
  }

  get currentCompanyIdObservable(): Observable<string | null> {
    return this.currentCompanyId$.asObservable();
  }

  // Obtener el ID de empresa actual (para uso en servicios)
  getCurrentCompanyId(): string | null {
    return this.currentCompanyId$.value;
  }

  // Verificar si el usuario actual puede impersonar empresas
  canImpersonate(): Observable<boolean> {
    return this.authService.currentUser$.pipe(
      map(user => !!(user && user.role === 'god'))
    );
  }

  // Cambiar a una empresa específica (solo para god users)
  async switchToCompany(companyId: string): Promise<void> {
    console.log('🔄 CompanyContextService.switchToCompany iniciado para:', companyId);

    try {
      console.log('🔍 Verificando permisos de impersonación...');
      const canImpersonate = await firstValueFrom(this.canImpersonate());
      console.log('🛡️ Permisos de impersonación:', canImpersonate);

      if (!canImpersonate) {
        throw new Error('No tienes permisos para impersonar empresas');
      }

      console.log('📡 Verificando que la empresa existe...');
      // Verificar que la empresa existe
      const company = await firstValueFrom(this.companyService.findOne(companyId));
      console.log('🏢 Empresa encontrada:', company?.name);

      if (!company) {
        throw new Error('Empresa no encontrada');
      }

      console.log('⚙️ Estableciendo contexto de impersonación...');
      // Establecer contexto de impersonación
      this.currentCompanyId$.next(companyId);
      this.isImpersonating$.next(true);

      // Guardar en localStorage para persistencia
      this.saveContext(companyId);

      console.log(`✅ Impersonando empresa exitosamente: ${company.name} (${companyId})`);

    } catch (error) {
      console.error('❌ Error en switchToCompany:', error);
      throw error;
    }
  }

  // Salir del modo impersonación
  exitImpersonation(): void {
    this.currentCompanyId$.next(null);
    this.isImpersonating$.next(false);

    // Limpiar localStorage
    this.clearContext();

    console.log('✅ Saliste del modo impersonación');
  }

  // Obtener empresa actual de forma síncrona
  getCurrentCompany(): Company | null {
    // Esta función retorna null si no hay contexto
    // Para obtener la empresa actual de forma asíncrona, usar currentCompany$
    return null;
  }

  // Verificar si está impersonando una empresa específica
  isImpersonatingCompany(companyId: string): Observable<boolean> {
    return combineLatest([
      this.isImpersonating$,
      this.currentCompanyId$
    ]).pipe(
      map(([isImpersonating, currentId]) =>
        isImpersonating && currentId === companyId
      )
    );
  }

  // Persistencia en localStorage
  private saveContext(companyId: string): void {
    const context = {
      companyId,
      timestamp: Date.now()
    };
    localStorage.setItem('god_company_context', JSON.stringify(context));
  }

  private restoreContext(): void {
    try {
      const stored = localStorage.getItem('god_company_context');
      if (stored) {
        const context = JSON.parse(stored);

        // Verificar que no sea muy viejo (máximo 24 horas)
        const maxAge = 24 * 60 * 60 * 1000; // 24 horas
        if (Date.now() - context.timestamp < maxAge) {
          // Verificar permisos antes de restaurar
          this.canImpersonate().subscribe(canImpersonate => {
            if (canImpersonate && context.companyId) {
              this.currentCompanyId$.next(context.companyId);
              this.isImpersonating$.next(true);
              console.log('🔄 Contexto de empresa restaurado:', context.companyId);
            }
          });
        } else {
          this.clearContext();
        }
      }
    } catch (error) {
      console.error('Error restaurando contexto:', error);
      this.clearContext();
    }
  }

  private clearContext(): void {
    localStorage.removeItem('god_company_context');
  }

  // Limpiar contexto al hacer logout
  clearOnLogout(): void {
    this.exitImpersonation();
  }
}